import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Search, Plus, Building2, Check, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import api, { formatApiErrorDetail } from "../../lib/api";

const input = "w-full bg-brand-surface border border-brand-border rounded-md px-3 py-2 text-sm text-brand-ink focus:outline-none focus:border-brand-primary transition-colors";
const EMPTY = { company_name: "", location: "", contact_name: "", contact_email: "", crm_lead_id: null };

function CompanySearch({ onPick, onAdd }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => api.get("/onboarding/crm-search", { params: { q } }).then(({ data }) => setResults(data)).catch(() => setResults([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="h-3.5 w-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-brand-tertiary" />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search CRM by company, contact or email" data-testid="onb-crm-search" className={`${input} pl-8`} />
      </div>
      {results && (
        <div className="border border-brand-border rounded-md bg-white max-h-64 overflow-y-auto divide-y divide-brand-border" data-testid="onb-crm-results">
          {results.map((r) => (
            <button type="button" key={`${r.lead_id}-${r.location}`} onClick={() => onPick(r)} data-testid={`onb-crm-result-${r.lead_id}`}
              className="w-full text-left px-3 py-2.5 hover:bg-brand-hover transition-colors flex items-start gap-2">
              <Building2 className="h-4 w-4 mt-0.5 text-brand-tertiary shrink-0" />
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-brand-ink truncate">{r.company}</span>
                <span className="block text-xs text-brand-muted truncate">{[r.contact_name, r.contact_email, r.location, r.stage].filter(Boolean).join(" · ")}</span>
              </span>
              {r.onboarding_id && <span className="text-[10px] text-amber-800 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5 shrink-0">Has a link</span>}
            </button>
          ))}
          {!results.length && <p className="px-3 py-2.5 text-xs text-brand-muted" data-testid="onb-crm-none">No CRM company matches "{q}".</p>}
          <button type="button" onClick={() => onAdd(q.trim())} data-testid="onb-crm-add-new"
            className="w-full text-left px-3 py-2.5 hover:bg-brand-hover transition-colors flex items-center gap-2 text-sm text-brand-ink">
            <Plus className="h-4 w-4 text-brand-tertiary" /> Add "{q.trim()}" as a new company
          </button>
        </div>
      )}
    </div>
  );
}

export function NewOnboardingDialog({ open, onOpenChange, locations, onCreated }) {
  const [f, setF] = useState(EMPTY);
  const [picked, setPicked] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setF(EMPTY); setPicked(null); } }, [open]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const pick = (r) => {
    setF({ company_name: r.company, location: locations.includes(r.location) ? r.location : "", contact_name: r.contact_name || "", contact_email: r.contact_email || "", crm_lead_id: r.lead_id });
    setPicked(r);
  };
  const addNew = (name) => { setF({ ...EMPTY, company_name: name }); setPicked({ isNew: true }); };
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/onboarding", { ...f, contact_name: f.contact_name || null, contact_email: f.contact_email || null });
      toast.success("Onboarding link created");
      onCreated(data);
    } catch (err) { toast.error("Could not create", { description: formatApiErrorDetail(err.response?.data?.detail) }); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-brand-page border-brand-border" data-testid="onb-new-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-light text-brand-ink">New onboarding link</DialogTitle>
          <DialogDescription className="text-xs text-brand-muted">Find the member company in the CRM, or add it if it isn't there.</DialogDescription>
        </DialogHeader>
        {!picked ? <CompanySearch onPick={pick} onAdd={addNew} /> : (
          <form onSubmit={submit} className="space-y-3">
            <div className="flex items-center gap-2 text-xs text-brand-muted" data-testid="onb-picked-source">
              {picked.isNew ? <><Plus className="h-3.5 w-3.5" /> New company (not in CRM)</> : <><Check className="h-3.5 w-3.5 text-emerald-700" /> From CRM{picked.stage ? ` · ${picked.stage}` : ""}</>}
              <button type="button" onClick={() => { setPicked(null); setF(EMPTY); }} data-testid="onb-change-company" className="ml-auto inline-flex items-center gap-1 text-brand-ink hover:underline"><X className="h-3 w-3" /> Change</button>
            </div>
            {picked.onboarding_id && <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2" data-testid="onb-existing-warning">This company already has an onboarding link. You can still create another one, e.g. for a second location.</p>}
            <input required placeholder="Member company name" value={f.company_name} onChange={set("company_name")} data-testid="onb-new-company" className={input} />
            <select required value={f.location} onChange={set("location")} data-testid="onb-new-location" className={input}>
              <option value="" disabled>Location</option>
              {locations.map((l) => <option key={l}>{l}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Contact name (optional)" value={f.contact_name} onChange={set("contact_name")} data-testid="onb-new-contact-name" className={input} />
              <input type="email" placeholder="Contact email (optional)" value={f.contact_email} onChange={set("contact_email")} data-testid="onb-new-contact-email" className={input} />
            </div>
            <button type="submit" disabled={busy} data-testid="onb-new-submit" className="w-full bg-brand-ink text-brand-page rounded-full py-2.5 text-sm hover:opacity-90 transition-opacity disabled:opacity-50">
              {busy ? "Creating…" : "Create link"}
            </button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
